import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import {
  getProject, createRequirement, updateRequirement, deleteRequirement,
  createTestCase, updateTestCase, deleteTestCase, downloadExcel, downloadJson, downloadPdf,
  syncToSquashStream, deleteFromSquash, verifySquashSync, safeErrorMessage, getUsersList,
  syncRequirementToSquash, syncTestCaseToSquash,
  reorderRequirements, reorderTestCases,
  getProjectTags, createTag, updateTag, deleteTag, setTestCaseTags,
} from '../services/api';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import RequirementForm from '../components/RequirementForm';
import TestCaseForm from '../components/TestCaseForm';
import RequirementItem from '../components/RequirementItem';
import AITargetedModal from '../components/AITargetedModal';
import AuditLogModal from '../components/AuditLogModal';
import ImportModal from '../components/ImportModal';
import CommentThread from '../components/CommentThread';
import ObservationModal from '../components/ObservationModal';
import Modal, { ConfirmModal } from '../components/Modal';
import { useToast } from '../App';
import { useAuth } from '../contexts/AuthContext';
import { HiOutlinePlus, HiOutlineDocumentDownload, HiOutlineEye, HiOutlineSparkles, HiOutlinePencil, HiOutlineTrash, HiOutlineClipboardList, HiOutlineRefresh, HiOutlineUpload, HiOutlineFilter, HiOutlineTag } from 'react-icons/hi';
import { FiFileText, FiCode, FiCheckCircle, FiXCircle, FiAlertTriangle, FiClock, FiMinusCircle, FiUser } from 'react-icons/fi';

async function doSquashDelete(projectId, addToast) {
  try {
    const { data } = await deleteFromSquash(projectId);
    if (data.project_deleted) {
      addToast('success', `Projet "${data.project_name}" supprimé de Squash TM.`, 'Suppression Squash TM');
    } else {
      const errs = data.errors?.join(' — ') || 'Projet introuvable dans Squash TM.';
      addToast('warning', errs, 'Suppression Squash TM');
    }
  } catch (err) {
    addToast('error', safeErrorMessage(err, 'Erreur lors de la suppression Squash TM.'), 'Erreur');
  }
}

async function doSquashVerify(projectId, addToast) {
  try {
    const { data } = await verifySquashSync(projectId);
    if (data.squash_project_id) {
      const reqFolders = data.requirement_folders?.map(f => `"${f.name}"`).join(', ') || '—';
      addToast(
        'info',
        `${data.requirements} exigences, ${data.test_cases} cas de test, ${data.campaigns} campagne(s) — dossiers : ${reqFolders}`,
        `Squash TM : projet "${data.project_name}"`,
      );
    } else {
      addToast('warning', `Projet "${data.project_name}" introuvable dans Squash TM.`, 'Vérification Squash TM');
    }
    return data;
  } catch (err) {
    addToast('error', safeErrorMessage(err, 'Erreur de vérification Squash TM.'), 'Erreur');
    return { errors: [safeErrorMessage(err, 'Erreur de vérification Squash TM.')] };
  }
}

function formatSyncParts(data, keys) {
  return keys.filter(([k]) => data[k]).map(([, label]) => label).join(', ') || 'Synchronisation terminee.';
}

async function doSyncRequirement(projectId, reqId, reqLabel, addToast) {
  try {
    const { data } = await syncRequirementToSquash(projectId, reqId);
    const errors = data.errors || [];
    if (errors.length > 0) {
      addToast('warning', `Sync partielle : ${errors.join(' — ')}`, `Sync exigence ${reqLabel}`);
    } else {
      const msg = formatSyncParts(data, [
        ['requirements_created', `${data.requirements_created} exigence(s) creee(s)`],
        ['requirements_updated', `${data.requirements_updated} exigence(s) mise(s) a jour`],
        ['test_cases_created', `${data.test_cases_created} CT cree(s)`],
        ['test_cases_updated', `${data.test_cases_updated} CT mis a jour`],
      ]);
      addToast('success', msg, `Sync exigence ${reqLabel}`);
    }
  } catch (err) {
    addToast('error', safeErrorMessage(err, 'Erreur de synchronisation Squash TM.'), 'Erreur');
  }
}

async function doSyncTestCase(projectId, tcId, tcLabel, addToast) {
  try {
    const { data } = await syncTestCaseToSquash(projectId, tcId);
    const errors = data.errors || [];
    if (errors.length > 0) {
      addToast('warning', `Sync partielle : ${errors.join(' — ')}`, `Sync CT ${tcLabel}`);
    } else {
      const msg = formatSyncParts(data, [
        ['test_cases_created', 'CT cree dans Squash TM'],
        ['test_cases_updated', 'CT mis a jour dans Squash TM'],
      ]);
      addToast('success', msg, `Sync CT ${tcLabel}`);
    }
  } catch (err) {
    addToast('error', safeErrorMessage(err, 'Erreur de synchronisation Squash TM.'), 'Erreur');
  }
}

function scrollToHighlight(tab, highlightId, project, setOpenAccordion) {
  if (tab === 'requirements') {
    const reqId = Number.parseInt(highlightId);
    setOpenAccordion(reqId);
    setTimeout(() => {
      const el = document.getElementById(`req-${reqId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
  } else if (tab === 'tests') {
    const tcId = Number.parseInt(highlightId);
    const parentReq = project.requirements?.find(r =>
      r.test_cases?.some(tc => tc.id === tcId)
    );
    if (parentReq) {
      setOpenAccordion(parentReq.id);
      setTimeout(() => {
        const el = document.getElementById(`tc-${tcId}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }
}

function processSyncEvent(event, handlers) {
  const { setSyncStatus, setSyncReqTotal, setSyncTcTotal, setSyncLog, setSyncReqCount, setSyncTcCount, setSyncDone, setSyncReport, addToast } = handlers;
  const appendLog = (msg) => setSyncLog((prev) => [...prev, msg]);

  switch (event.type) {
    case 'start':
      setSyncStatus('Connexion à Squash TM...');
      setSyncReqTotal(event.total_requirements || 0);
      setSyncTcTotal(event.total_test_cases || 0);
      appendLog('Démarrage de la synchronisation...');
      break;
    case 'project': {
      const msg = event.created ? `Projet "${event.name}" créé dans Squash TM` : `Projet "${event.name}" trouvé dans Squash TM`;
      setSyncStatus(msg);
      appendLog(msg);
      break;
    }
    case 'folder':
      appendLog(`Dossier ${event.kind} "${event.name}" créé`);
      break;
    case 'requirement': {
      setSyncReqCount(event.index);
      const msg = `Exigence ${event.index}/${event.total} : ${event.exg_id} - ${event.name}`;
      setSyncStatus(msg);
      appendLog((event.success ? '\u2713 ' : '\u2717 ') + msg);
      break;
    }
    case 'test_case': {
      setSyncTcCount(event.index);
      const msg = `Cas de test ${event.index}/${event.total} : ${event.ct_id} - ${event.name}`;
      setSyncStatus(msg);
      appendLog((event.success ? '\u2713 ' : '\u2717 ') + msg);
      break;
    }
    case 'phase':
      setSyncStatus(event.message);
      appendLog(event.message);
      break;
    case 'done': {
      setSyncDone(true);
      setSyncReport(event.report);
      setSyncStatus('Synchronisation terminée !');
      const r = event.report;
      const doneMsg = `Sync terminée : ${r.requirements_created} exigences, ${r.test_cases_created} cas de test, ${r.coverages_created} liens.`
        + (r.errors?.length ? ` (${r.errors.length} erreur(s))` : '');
      addToast(r.errors?.length ? 'warning' : 'success', doneMsg);
      break;
    }
    case 'error':
      setSyncDone(true);
      setSyncStatus(`Erreur : ${event.message}`);
      appendLog(`ERREUR : ${event.message}`);
      addToast('error', event.message);
      break;
    default:
      break;
  }
}

export default function ProjectDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const addToast = useToast();
  const { isAdmin, user: currentUser } = useAuth();
  const [project, setProject] = useState(null);
  const [showReqModal, setShowReqModal] = useState(false);
  const [editingReq, setEditingReq] = useState(null);
  const [showTCModal, setShowTCModal] = useState(null); // reqId
  const [editingTC, setEditingTC] = useState(null);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncModal, setSyncModal] = useState(false);
  const [syncStatus, setSyncStatus] = useState(''); // current message
  const [syncReqCount, setSyncReqCount] = useState(0);
  const [syncReqTotal, setSyncReqTotal] = useState(0);
  const [syncTcCount, setSyncTcCount] = useState(0);
  const [syncTcTotal, setSyncTcTotal] = useState(0);
  const [syncLog, setSyncLog] = useState([]);
  const [syncDone, setSyncDone] = useState(false);
  const [syncReport, setSyncReport] = useState(null);
  const [deletingSquash, setDeletingSquash] = useState(false);
  const [confirmSquashDelete, setConfirmSquashDelete] = useState(false);
  const [verifyingSquash, setVerifyingSquash] = useState(false);
  const [squashVerifyResult, setSquashVerifyResult] = useState(null);
  const [syncingItemId, setSyncingItemId] = useState(null);
  const [aiModal, setAiModal] = useState(null);
  const [auditModal, setAuditModal] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [commentThread, setCommentThread] = useState(null);
  const [observationModal, setObservationModal] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [filterMyTC, setFilterMyTC] = useState(false);
  const [selectedEnv, setSelectedEnv] = useState('DEV');
  const [tcPages, setTcPages] = useState({});
  const [projectTags, setProjectTags] = useState([]);
  const [showTagManager, setShowTagManager] = useState(false);
  const [filterTag, setFilterTag] = useState(null);
  const [tagTCModal, setTagTCModal] = useState(null);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#6B7A90');
  const [editingTag, setEditingTag] = useState(null);

  const loadProject = useCallback(async () => {
    const { data } = await getProject(id);
    setProject(data);
  }, [id]);

  useEffect(() => { loadProject(); }, [loadProject]);

  // Auto-refresh toutes les 20 secondes
  useEffect(() => {
    const interval = setInterval(() => {
      loadProject().catch(() => {});
    }, 20000);
    return () => clearInterval(interval);
  }, [loadProject]);

  useEffect(() => {
    getUsersList().then(({ data }) => setUsersList(data)).catch(() => {});
  }, []);

  const loadTags = useCallback(() => {
    getProjectTags(id).then(({ data }) => setProjectTags(data)).catch(() => {});
  }, [id]);

  useEffect(() => { loadTags(); }, [loadTags]);

  // Gestion navigation depuis la recherche globale (Ctrl+K)
  useEffect(() => {
    if (!project) return;
    const highlightId = searchParams.get('highlight');
    if (!highlightId) return;
    scrollToHighlight(searchParams.get('tab'), highlightId, project, setOpenAccordion);
    setSearchParams({}, { replace: true });
  }, [project, searchParams, setSearchParams]);

  // Acces lecture seule pour utilisateurs affectes (non-owner, non-admin)
  const readOnly = project?.is_readonly === true;
  const canEdit = isAdmin || !readOnly;

  const ENVIRONMENTS = [
    { value: 'DEV', label: 'DEV' },
    { value: 'RECETTE', label: 'Recette' },
    { value: 'PROD', label: 'Production' },
  ];

  const getEnvStatut = (tc) => {
    if (!tc.executions || tc.executions.length === 0) return tc.statut;
    const exec = tc.executions.find(e => e.environment === selectedEnv);
    return exec ? exec.statut : 'NT';
  };

  const getEnvCommentaire = (tc) => {
    if (!tc.executions || tc.executions.length === 0) return tc.commentaire;
    const exec = tc.executions.find(e => e.environment === selectedEnv);
    return exec ? exec.commentaire : '';
  };

  if (!project) return (
    <div className="loading-spinner">
      <output className="spinner-border"></output>
      <span>Chargement du projet...</span>
    </div>
  );

  const handleCreateReq = async (form) => {
    await createRequirement(id, form);
    setShowReqModal(false);
    loadProject();
    addToast('success', 'Exigence créée avec succès.');
  };

  const handleUpdateReq = async (form) => {
    await updateRequirement(id, editingReq.id, form);
    setEditingReq(null);
    loadProject();
    addToast('success', 'Exigence modifiée.');
  };

  const handleDeleteReq = async () => {
    if (!confirmAction) return;
    await deleteRequirement(id, confirmAction.id);
    setConfirmAction(null);
    loadProject();
    addToast('success', 'Exigence supprimée.');
  };

  const handleCreateTC = async (reqId, form) => {
    await createTestCase(id, reqId, form);
    setShowTCModal(null);
    loadProject();
    addToast('success', 'Cas de test créé.');
  };

  const handleUpdateTC = async (form) => {
    await updateTestCase(id, editingTC.id, form);
    setEditingTC(null);
    loadProject();
    addToast('success', 'Cas de test modifié.');
  };

  const handleDeleteTC = async () => {
    if (!confirmAction) return;
    await deleteTestCase(id, confirmAction.id);
    setConfirmAction(null);
    loadProject();
    addToast('success', 'Cas de test supprimé.');
  };

  const handleReqDragEnd = async (result) => {
    if (!result.destination || result.source.index === result.destination.index) return;
    const reqs = [...project.requirements];
    const [moved] = reqs.splice(result.source.index, 1);
    reqs.splice(result.destination.index, 0, moved);
    // Optimistic update
    setProject({ ...project, requirements: reqs });
    try {
      await reorderRequirements(id, reqs.map(r => r.id));
    } catch (err) {
      loadProject();
      addToast('error', safeErrorMessage(err, 'Erreur lors du reordonnancement'));
    }
  };

  const handleTCDragEnd = async (reqId, result) => {
    if (!result.destination || result.source.index === result.destination.index) return;
    const req = project.requirements.find(r => r.id === reqId);
    if (!req) return;
    const tcs = [...req.test_cases];
    const [moved] = tcs.splice(result.source.index, 1);
    tcs.splice(result.destination.index, 0, moved);
    // Optimistic update
    const updatedReqs = project.requirements.map(r =>
      r.id === reqId ? { ...r, test_cases: tcs } : r
    );
    setProject({ ...project, requirements: updatedReqs });
    try {
      await reorderTestCases(id, reqId, tcs.map(tc => tc.id));
    } catch (err) {
      loadProject();
      addToast('error', safeErrorMessage(err, 'Erreur lors du reordonnancement'));
    }
  };

  const handleAssign = async (tcId, assignedToId) => {
    try {
      await updateTestCase(id, tcId, { assigned_to_id: assignedToId || null });
      loadProject();
    } catch (err) {
      addToast('error', safeErrorMessage(err, 'Erreur lors de l\'assignation'));
    }
  };

  const handleAssignReq = async (reqId, assignedToId) => {
    try {
      await updateRequirement(id, reqId, { assigned_to_id: assignedToId || null });
      loadProject();
    } catch (err) {
      addToast('error', safeErrorMessage(err, 'Erreur lors de l\'assignation de l\'exigence'));
    }
  };

  const handleDownload = async (type) => {
    try {
      const downloadFns = { excel: () => downloadExcel(id, selectedEnv), pdf: () => downloadPdf(id, selectedEnv), json: () => downloadJson(id) };
      const downloadFn = downloadFns[type] || downloadFns.json;
      const { data, headers } = await downloadFn();
      const contentDisposition = headers['content-disposition'] || '';
      const filenameMatch = /filename="?([^"]+)"?/.exec(contentDisposition);
      const extMap = { excel: '.xlsx', json: '.json', pdf: '.pdf' };
      let filename = `${project?.name || 'project'}${extMap[type] || '.bin'}`;
      if (filenameMatch) {
        // Sanitize: strip path separators to prevent path traversal
        filename = filenameMatch[1].replaceAll(/[/\\]/g, '_');
      }
      const url = URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      addToast('error', safeErrorMessage(err, 'Erreur lors du téléchargement'));
    }
  };

  const syncEventHandlers = { setSyncStatus, setSyncReqTotal, setSyncTcTotal, setSyncLog, setSyncReqCount, setSyncTcCount, setSyncDone, setSyncReport, addToast };

  const handleSync = async () => {
    setSyncing(true);
    setSyncModal(true);
    setSyncDone(false);
    setSyncReport(null);
    setSyncStatus('Connexion à Squash TM...');
    setSyncReqCount(0);
    setSyncReqTotal(0);
    setSyncTcCount(0);
    setSyncTcTotal(0);
    setSyncLog([]);

    try {
      const response = await syncToSquashStream(id);
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        buffer = buffer.replaceAll('\r\n', '\n');

        while (buffer.includes('\n\n')) {
          const idx = buffer.indexOf('\n\n');
          const eventBlock = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);

          for (const line of eventBlock.split('\n')) {
            const trimmed = line.trim();
            if (!trimmed.startsWith('data: ')) continue;
            processSyncEvent(JSON.parse(trimmed.slice(6)), syncEventHandlers);
          }
        }
      }
    } catch (err) {
      setSyncDone(true);
      const msg = err.message || 'Erreur de synchronisation Squash TM';
      setSyncStatus(`Erreur : ${msg}`);
      addToast('error', msg);
    }
    setSyncing(false);
  };

  const handleSquashDelete = async () => {
    setConfirmSquashDelete(false);
    setDeletingSquash(true);
    await doSquashDelete(id, addToast);
    setDeletingSquash(false);
  };

  const handleSquashVerify = async () => {
    setVerifyingSquash(true);
    setSquashVerifyResult(null);
    const result = await doSquashVerify(id, addToast);
    setSquashVerifyResult(result);
    setVerifyingSquash(false);
  };

  const handleSyncRequirement = async (reqId, reqLabel) => {
    setSyncingItemId(`req-${reqId}`);
    await doSyncRequirement(id, reqId, reqLabel, addToast);
    setSyncingItemId(null);
  };

  const handleSyncTestCase = async (tcId, tcLabel) => {
    setSyncingItemId(`tc-${tcId}`);
    await doSyncTestCase(id, tcId, tcLabel, addToast);
    setSyncingItemId(null);
  };

  const totalTC = project.requirements.reduce((sum, r) => sum + r.test_cases.length, 0);
  const stats = { NT: 0, EC: 0, OK: 0, KO: 0, BL: 0 };
  const assignStats = {};
  project.requirements.forEach((r) =>
    r.test_cases.forEach((tc) => {
      const st = getEnvStatut(tc);
      stats[st] = (stats[st] || 0) + 1;
      if (tc.assigned_to_username) {
        assignStats[tc.assigned_to_username] = (assignStats[tc.assigned_to_username] || 0) + 1;
      }
    })
  );
  const progressPct = (val) => totalTC > 0 ? ((val / totalTC) * 100).toFixed(1) : 0;

  return (
    <div className="project-detail-page">
      {/* Fixed top zone */}
      <div className="project-detail-top">
        {/* Page Header */}
        <div className="page-header">
          <div>
            <h2>{project.name}</h2>
            <span className="project-code">{project.code}</span>
            {project.description && <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.85rem' }}>{project.description}</p>}
          </div>
          <div className="actions">
            <Link to={`/projects/${id}/preview`} className="btn btn-outline-info btn-sm"><HiOutlineEye /> Preview</Link>
            {canEdit && <Link to={`/projects/${id}/ai`} className="btn btn-outline-warning btn-sm"><HiOutlineSparkles /> IA</Link>}
            {canEdit && <button className="btn btn-outline-secondary btn-sm" disabled={syncing || deletingSquash} onClick={handleSync}>
              {syncing ? <><span className="spinner-border spinner-border-sm me-1"></span> Sync...</> : <><HiOutlineRefresh /> Sync-SquashTm</>}
            </button>}
            {canEdit && <button
              className="btn btn-outline-danger btn-sm"
              disabled={syncing || deletingSquash}
              onClick={() => setConfirmSquashDelete(true)}
              title="Supprimer ce projet depuis Squash TM"
            >
              {deletingSquash
                ? <><span className="spinner-border spinner-border-sm me-1"></span> Suppression...</>
                : <><HiOutlineTrash /> Supprimer de Squash</>}
            </button>}
            {canEdit && <button onClick={() => handleDownload('excel')} className="btn btn-outline-success btn-sm"><HiOutlineDocumentDownload /> Excel</button>}
            {canEdit && <button onClick={() => handleDownload('json')} className="btn btn-outline-primary btn-sm"><FiCode /> JSON</button>}
            {canEdit && <button onClick={() => handleDownload('pdf')} className="btn btn-outline-danger btn-sm"><HiOutlineDocumentDownload /> PDF</button>}
            {canEdit && <button onClick={() => setShowImportModal(true)} className="btn btn-outline-info btn-sm"><HiOutlineUpload /> Importer</button>}
          </div>
        </div>

        {/* Environment Selector */}
        <div className="d-flex align-items-center gap-2 mb-3">
          <span style={{ fontSize: '0.82rem', fontWeight: 500, color: 'var(--gray-600)' }}>Environnement :</span>
          {ENVIRONMENTS.map((env) => (
            <button
              key={env.value}
              className={`btn btn-sm ${selectedEnv === env.value ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setSelectedEnv(env.value)}
            >
              {env.label}
            </button>
          ))}
        </div>

        {/* Stats Row */}
        <div className="row g-3 mb-3">
          <div className="col"><div className="stat-card"><div className="stat-icon stat-icon-navy"><FiFileText /></div><div className="stat-content"><div className="stat-number">{project.requirements.length}</div><div className="stat-label">Exigences</div></div></div></div>
          <div className="col"><div className="stat-card"><div className="stat-icon stat-icon-info"><HiOutlineClipboardList /></div><div className="stat-content"><div className="stat-number">{totalTC}</div><div className="stat-label">Cas de Test</div></div></div></div>
          <div className="col"><div className="stat-card"><div className="stat-icon stat-icon-green"><FiCheckCircle /></div><div className="stat-content"><div className="stat-number">{stats.OK}</div><div className="stat-label">Réussis</div></div></div></div>
          <div className="col"><div className="stat-card"><div className="stat-icon stat-icon-danger"><FiXCircle /></div><div className="stat-content"><div className="stat-number">{stats.KO}</div><div className="stat-label">Échoués</div></div></div></div>
          <div className="col"><div className="stat-card"><div className="stat-icon stat-icon-warning"><FiAlertTriangle /></div><div className="stat-content"><div className="stat-number">{stats.BL}</div><div className="stat-label">Bloqués</div></div></div></div>
        </div>

        {/* Progress Bar */}
        {totalTC > 0 && (
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span style={{ fontSize: '0.78rem', fontWeight: 500, color: 'var(--gray-600)' }}>Progression des tests</span>
              <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>{stats.OK + stats.KO} / {totalTC} exécutés</span>
            </div>
            <div className="test-progress">
              <div className="progress-ok" style={{ width: `${progressPct(stats.OK)}%` }}></div>
              <div className="progress-ko" style={{ width: `${progressPct(stats.KO)}%` }}></div>
              <div className="progress-ec" style={{ width: `${progressPct(stats.EC)}%` }}></div>
              <div className="progress-bl" style={{ width: `${progressPct(stats.BL)}%` }}></div>
              <div className="progress-nt" style={{ width: `${progressPct(stats.NT)}%` }}></div>
            </div>
            <div className="d-flex gap-3 mt-1 flex-wrap align-items-center" style={{ fontSize: '0.7rem', color: 'var(--gray-500)' }}>
              <span><FiCheckCircle style={{ color: 'var(--green-500)' }} /> OK {stats.OK}</span>
              <span><FiXCircle style={{ color: 'var(--danger)' }} /> KO {stats.KO}</span>
              <span><FiClock style={{ color: 'var(--info)' }} /> EC {stats.EC}</span>
              <span><FiAlertTriangle style={{ color: 'var(--warning)' }} /> BL {stats.BL}</span>
              <span><FiMinusCircle style={{ color: 'var(--gray-400)' }} /> NT {stats.NT}</span>
              {Object.keys(assignStats).length > 0 && (
                <>
                  <span style={{ borderLeft: '1px solid var(--gray-300)', paddingLeft: '8px' }}><FiUser /> Charge :</span>
                  {Object.entries(assignStats).sort((a, b) => b[1] - a[1]).map(([username, count]) => (
                    <span key={username} className="meta-tag" style={{ fontSize: '0.68rem' }}>{username}: {count} CT</span>
                  ))}
                </>
              )}
            </div>
          </div>
        )}

        {/* New Requirement Button + Filter */}
        <div className="d-flex gap-2 align-items-center">
          {canEdit && <button className="btn btn-primary btn-sm" onClick={() => setShowReqModal(true)}>
            <HiOutlinePlus /> Nouvelle Exigence
          </button>}
          <button
            className={`btn btn-sm ${filterMyTC ? 'btn-info' : 'btn-outline-secondary'}`}
            onClick={() => { setFilterMyTC(!filterMyTC); setTcPages({}); }}
            title="Filtrer mes cas de test"
          >
            <HiOutlineFilter /> {filterMyTC ? 'Mes CT (actif)' : 'Mes CT'}
          </button>
          {canEdit && <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowTagManager(true)} title="Gerer les tags">
            <HiOutlineTag /> Tags
          </button>}
          {projectTags.length > 0 && (
            <div className="d-flex align-items-center gap-1 ms-2">
              <span style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>Filtre:</span>
              {filterTag && <button className="tag-chip-btn tag-chip-active" style={{ '--tag-color': projectTags.find(t => t.id === filterTag)?.color || '#6B7A90' }} onClick={() => { setFilterTag(null); setTcPages({}); }}>
                {projectTags.find(t => t.id === filterTag)?.name} &times;
              </button>}
              {!filterTag && projectTags.map(t => (
                <button key={t.id} className="tag-chip-btn" style={{ '--tag-color': t.color }} onClick={() => { setFilterTag(t.id); setTcPages({}); }}>
                  {t.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Scrollable accordion zone */}
      <div className="project-detail-scroll">
        <DragDropContext onDragEnd={handleReqDragEnd}>
          <Droppable droppableId="requirements">
            {(provided) => (
              <div className="accordion" id="reqAccordion" ref={provided.innerRef} {...provided.droppableProps}>
                {project.requirements.map((req, reqIndex) => (
                  <RequirementItem
                    key={req.id}
                    req={req}
                    reqIndex={reqIndex}
                    projectId={id}
                    isAdmin={isAdmin}
                    canEdit={canEdit}
                    openAccordion={openAccordion}
                    setOpenAccordion={setOpenAccordion}
                    editingReq={editingReq}
                    setEditingReq={setEditingReq}
                    handleUpdateReq={handleUpdateReq}
                    setConfirmAction={setConfirmAction}
                    setShowTCModal={setShowTCModal}
                    setAiModal={setAiModal}
                    setAuditModal={setAuditModal}
                    syncingItemId={syncingItemId}
                    handleSyncRequirement={handleSyncRequirement}
                    loadProject={loadProject}
                    usersList={usersList}
                    currentUser={currentUser}
                    filterMyTC={filterMyTC}
                    filterTag={filterTag}
                    tcPages={tcPages}
                    setTcPages={setTcPages}
                    projectTags={projectTags}
                    setTagTCModal={setTagTCModal}
                    editingTC={editingTC}
                    setEditingTC={setEditingTC}
                    handleUpdateTC={handleUpdateTC}
                    handleTCDragEnd={handleTCDragEnd}
                    handleAssign={handleAssign}
                    handleAssignReq={handleAssignReq}
                    getEnvStatut={getEnvStatut}
                    getEnvCommentaire={getEnvCommentaire}
                    selectedEnv={selectedEnv}
                    setObservationModal={setObservationModal}
                    setCommentThread={setCommentThread}
                    handleSyncTestCase={handleSyncTestCase}
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {project.requirements.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon"><FiFileText /></div>
            <p>{readOnly ? 'Aucun cas de test affecte pour le moment.' : 'Aucune exigence. Ajoutez-en une ou utilisez la generation IA.'}</p>
            {canEdit && (
              <div className="d-flex gap-2 justify-content-center mt-3">
                <button className="btn btn-primary btn-sm" onClick={() => setShowReqModal(true)}><HiOutlinePlus /> Nouvelle Exigence</button>
                <Link to={`/projects/${id}/ai`} className="btn btn-outline-warning btn-sm"><HiOutlineSparkles /> Generation IA</Link>
              </div>
            )}
          </div>
        )}
      </div>{/* end project-detail-scroll */}

      {/* Modal Nouvelle Exigence */}
      <Modal show={showReqModal} onClose={() => setShowReqModal(false)} title="Nouvelle Exigence" icon={<HiOutlinePlus />} size="lg">
        <RequirementForm onSubmit={handleCreateReq} onCancel={() => setShowReqModal(false)} />
      </Modal>

      {/* Modal Nouveau Cas de Test */}
      <Modal show={!!showTCModal} onClose={() => setShowTCModal(null)} title="Nouveau Cas de Test" icon={<HiOutlinePlus />} size="lg">
        <TestCaseForm onSubmit={(form) => handleCreateTC(showTCModal, form)} onCancel={() => setShowTCModal(null)} />
      </Modal>

      {/* Confirm Delete */}
      <ConfirmModal
        show={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={confirmAction?.type === 'req' ? handleDeleteReq : handleDeleteTC}
        title={confirmAction?.type === 'req' ? 'Supprimer l\'exigence' : 'Supprimer le cas de test'}
        message={confirmAction?.type === 'req'
          ? 'Cette action supprimera l\'exigence et tous ses cas de test. Continuer ?'
          : 'Voulez-vous supprimer ce cas de test ?'}
        confirmText="Supprimer"
      />

      {/* Confirm Squash TM Delete */}
      <ConfirmModal
        show={confirmSquashDelete}
        onClose={() => setConfirmSquashDelete(false)}
        onConfirm={handleSquashDelete}
        title="Supprimer de Squash TM"
        message={`Supprimer le projet "${project?.name}" depuis Squash TM ? Cette action supprime définitivement toutes les exigences, cas de test, campagnes et dashboards dans Squash TM. Votre projet local reste intact.`}
        confirmText="Supprimer de Squash TM"
      />

      {/* AI Targeted Modal (Enrich / Improve) */}
      {aiModal && (
        <AITargetedModal
          show={!!aiModal}
          onClose={() => setAiModal(null)}
          mode={aiModal.mode}
          projectId={id}
          requirement={aiModal.requirement}
          testCase={aiModal.testCase}
          onAccepted={() => { loadProject(); setAiModal(null); }}
        />
      )}

      {/* Import Modal */}
      <ImportModal
        show={showImportModal}
        onClose={() => setShowImportModal(false)}
        projectId={id}
        onImported={() => { loadProject(); addToast('success', 'Import termine avec succes.'); }}
      />

      {/* Audit Log Modal */}
      <AuditLogModal
        show={!!auditModal}
        onClose={() => setAuditModal(null)}
        projectId={id}
        entityType={auditModal?.entityType}
        entityId={auditModal?.entityId}
        entityLabel={auditModal?.entityLabel}
      />

      {/* Observation Modal (commentaire + screenshots) */}
      <ObservationModal
        show={!!observationModal}
        onClose={() => setObservationModal(null)}
        testCase={observationModal?.testCase}
        projectId={id}
        commentaire={observationModal?.commentaire}
      />

      {/* Discussion Modal */}
      <CommentThread
        show={!!commentThread}
        onClose={() => setCommentThread(null)}
        projectId={commentThread?.projectId}
        testCase={commentThread?.testCase}
        onCommentCountChange={() => loadProject()}
      />

      {/* Tag Manager Modal */}
      <Modal show={showTagManager} onClose={() => { setShowTagManager(false); setEditingTag(null); setNewTagName(''); }} title="Gestion des Tags" icon={<HiOutlineTag />}>
        <div style={{ fontSize: '0.85rem' }}>
          <div className="d-flex gap-2 mb-3">
            <input
              className="form-control form-control-sm"
              placeholder="Nom du tag"
              value={editingTag ? editingTag.name : newTagName}
              onChange={(e) => editingTag ? setEditingTag({ ...editingTag, name: e.target.value }) : setNewTagName(e.target.value)}
              maxLength={50}
            />
            <input
              type="color"
              className="form-control form-control-sm form-control-color"
              style={{ width: '40px', padding: '2px' }}
              value={editingTag ? editingTag.color : newTagColor}
              onChange={(e) => editingTag ? setEditingTag({ ...editingTag, color: e.target.value }) : setNewTagColor(e.target.value)}
            />
            {editingTag ? (
              <>
                <button className="btn btn-primary btn-sm" onClick={async () => {
                  if (!editingTag.name.trim()) return;
                  try {
                    await updateTag(id, editingTag.id, { name: editingTag.name.trim(), color: editingTag.color });
                    loadTags(); loadProject(); setEditingTag(null);
                    addToast('success', 'Tag modifie.');
                  } catch (err) { addToast('error', safeErrorMessage(err)); }
                }}>Sauver</button>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setEditingTag(null)}>Annuler</button>
              </>
            ) : (
              <button className="btn btn-primary btn-sm" disabled={!newTagName.trim()} onClick={async () => {
                try {
                  await createTag(id, { name: newTagName.trim(), color: newTagColor });
                  setNewTagName(''); loadTags();
                  addToast('success', 'Tag cree.');
                } catch (err) { addToast('error', safeErrorMessage(err)); }
              }}>Ajouter</button>
            )}
          </div>
          {projectTags.length === 0 && <p className="text-muted">Aucun tag. Creez-en un ci-dessus.</p>}
          <div className="d-flex flex-wrap gap-2">
            {projectTags.map(t => (
              <div key={t.id} className="tag-manager-item" style={{ '--tag-color': t.color }}>
                <span className="tag-chip" style={{ '--tag-color': t.color }}>{t.name}</span>
                <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.65rem', padding: '0 3px' }} onClick={() => setEditingTag({ id: t.id, name: t.name, color: t.color })} title="Modifier"><HiOutlinePencil /></button>
                <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.65rem', padding: '0 3px', color: 'var(--danger)' }} onClick={async () => {
                  try { await deleteTag(id, t.id); loadTags(); loadProject(); addToast('success', 'Tag supprime.'); }
                  catch (err) { addToast('error', safeErrorMessage(err)); }
                }} title="Supprimer"><HiOutlineTrash /></button>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {/* Tag Assignment Modal */}
      <Modal show={!!tagTCModal} onClose={() => setTagTCModal(null)} title="Tags du cas de test" icon={<HiOutlineTag />}>
        {tagTCModal && (
          <div style={{ fontSize: '0.85rem' }}>
            {projectTags.length === 0 && <p className="text-muted">Aucun tag disponible. Creez-en d'abord.</p>}
            <div className="d-flex flex-wrap gap-2">
              {projectTags.map(t => {
                const isSelected = tagTCModal.currentTagIds.includes(t.id);
                return (
                  <button
                    key={t.id}
                    className={`tag-chip-btn ${isSelected ? 'tag-chip-selected' : ''}`}
                    style={{ '--tag-color': t.color }}
                    onClick={() => {
                      const newIds = isSelected
                        ? tagTCModal.currentTagIds.filter(tid => tid !== t.id)
                        : [...tagTCModal.currentTagIds, t.id];
                      setTagTCModal({ ...tagTCModal, currentTagIds: newIds });
                    }}
                  >
                    {isSelected && <FiCheckCircle style={{ fontSize: '0.7rem' }} />} {t.name}
                  </button>
                );
              })}
            </div>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button className="btn btn-outline-secondary btn-sm" onClick={() => setTagTCModal(null)}>Annuler</button>
              <button className="btn btn-primary btn-sm" onClick={async () => {
                try {
                  await setTestCaseTags(id, tagTCModal.tcId, tagTCModal.currentTagIds);
                  loadProject(); setTagTCModal(null);
                } catch (err) { addToast('error', safeErrorMessage(err)); }
              }}>Appliquer</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Sync Progress Modal */}
      <Modal
        show={syncModal}
        onClose={() => { if (syncDone) { setSyncModal(false); setSyncReport(null); setSquashVerifyResult(null); } }}
        title="Synchronisation Squash TM"
        icon={<HiOutlineRefresh />}
        size="lg"
        footer={syncDone ? (
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-info btn-sm"
              onClick={handleSquashVerify}
              disabled={verifyingSquash}
              title="Vérifier ce qui est dans Squash TM (lecture DB directe)"
            >
              {verifyingSquash
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Vérification...</>
                : 'Vérifier dans Squash TM'}
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => { setSyncModal(false); setSyncReport(null); setSquashVerifyResult(null); }}>Fermer</button>
          </div>
        ) : null}
      >
        <div style={{ fontSize: '0.85rem' }}>
          {/* Current status */}
          <div className="d-flex align-items-center gap-2 mb-3">
            {!syncDone && <span className="spinner-border spinner-border-sm"></span>}
            <strong>{syncStatus}</strong>
          </div>

          {/* Progress bars */}
          {syncReqTotal > 0 && (
            <div className="mb-2">
              <div className="d-flex justify-content-between" style={{ fontSize: '0.78rem' }}>
                <span>Exigences</span>
                <span>{syncReqCount} / {syncReqTotal}</span>
              </div>
              <div className="progress" style={{ height: '6px' }}>
                <div className="progress-bar bg-primary" style={{ width: `${(syncReqCount / syncReqTotal) * 100}%`, transition: 'width 0.3s' }}></div>
              </div>
            </div>
          )}
          {syncTcTotal > 0 && (
            <div className="mb-3">
              <div className="d-flex justify-content-between" style={{ fontSize: '0.78rem' }}>
                <span>Cas de test</span>
                <span>{syncTcCount} / {syncTcTotal}</span>
              </div>
              <div className="progress" style={{ height: '6px' }}>
                <div className="progress-bar bg-success" style={{ width: `${(syncTcCount / syncTcTotal) * 100}%`, transition: 'width 0.3s' }}></div>
              </div>
            </div>
          )}

          {/* Report summary */}
          {syncReport && (
            <div className={`alert ${syncReport.errors?.length ? 'alert-warning' : 'alert-success'} py-2 mb-3`} style={{ fontSize: '0.8rem' }}>
              <strong>Résultat :</strong> {syncReport.requirements_created} exigences, {syncReport.test_cases_created} cas de test, {syncReport.coverages_created} couvertures
              {syncReport.campaign_created && `, 1 campagne, ${syncReport.iterations_created} itérations`}
              {syncReport.errors?.length > 0 && <span className="text-danger"> — {syncReport.errors.length} erreur(s)</span>}
            </div>
          )}

          {/* Scrollable log */}
          <div style={{
            maxHeight: '220px', overflowY: 'auto', background: 'var(--gray-50, #f8f9fa)',
            border: '1px solid var(--gray-200, #e9ecef)', borderRadius: '6px',
            padding: '8px 12px', fontSize: '0.75rem', fontFamily: 'monospace',
          }}>
            {syncLog.map((line) => (
              <div key={line} style={{ color: line.startsWith('ERREUR') || line.startsWith('\u2717') ? 'var(--danger, #dc3545)' : 'inherit' }}>
                {line}
              </div>
            ))}
            {syncLog.length === 0 && <div style={{ color: 'var(--gray-400, #adb5bd)' }}>En attente...</div>}
          </div>

          {/* Verify result */}
          {squashVerifyResult && (
            <div className={`alert ${squashVerifyResult.errors?.length && !squashVerifyResult.squash_project_id ? 'alert-danger' : 'alert-info'} py-2 mt-3`} style={{ fontSize: '0.8rem' }}>
              {squashVerifyResult.errors?.length && !squashVerifyResult.squash_project_id ? (
                <><strong>Erreur :</strong> {squashVerifyResult.errors.join(' — ')}</>
              ) : (
                <>
                  <strong>Squash TM — Projet "{squashVerifyResult.project_name}" (id={squashVerifyResult.squash_project_id}) :</strong>
                  <ul className="mb-0 mt-1" style={{ paddingLeft: '1.2rem' }}>
                    <li>{squashVerifyResult.requirements} exigences dans {squashVerifyResult.requirement_folders?.length || 0} dossier(s) :
                      {squashVerifyResult.requirement_folders?.map(f => ` "${f.name}"`).join(', ')}</li>
                    <li>{squashVerifyResult.test_cases} cas de test dans {squashVerifyResult.test_case_folders?.length || 0} dossier(s) :
                      {squashVerifyResult.test_case_folders?.map(f => ` "${f.name}"`).join(', ')}</li>
                    <li>{squashVerifyResult.campaigns} campagne(s)</li>
                  </ul>
                  {squashVerifyResult.errors?.length > 0 && (
                    <div className="text-warning mt-1">{squashVerifyResult.errors.join(' — ')}</div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
